import type { GeometryElement, Point } from 'jsxgraph';
import type { ElementDisplayConfig, PointElementParams, PrimitiveElementDef } from '../../graphpad.model';
import type { CreatedElement, ElementContext, ElementCreator } from '../element.model';
import { resolveFeatureStyle, resolveFeatureVisibility } from '../features/feature-style.model';

/**
 * Creates a standalone point primitive.
 *
 * Params define identity: x/y expressions, optional name.
 * Display defines styling: color, size, visible.
 *
 * Exposes computed values: { x, y }
 *
 * When both x and y are literal numbers, the point is parameterized:
 * getParams/setParams are exposed so UPDATE_PARAM can animate position changes.
 *
 * When `fixed: false` is set in the display config and the point has literal
 * coordinates, the point becomes user-draggable. A draggable point is created
 * with literal coords (making it a JSXGraph "free" point) so the user can
 * drag it. Drag events sync the internal position state and notify the widget
 * via onParamChange so reactions and dynamic labels update in real-time.
 */
export class PointCreator implements ElementCreator<PrimitiveElementDef> {
  readonly types = ['point'];

  create(
    id: string,
    def: PrimitiveElementDef,
    display: ElementDisplayConfig | undefined,
    context: ElementContext,
  ): CreatedElement {
    const { elementFactory, featureTheme } = context;
    const params = def.params as PointElementParams;
    const objects: GeometryElement[] = [];

    const { valueContext } = context;
    if (!valueContext) {
      throw new Error(`PointCreator requires valueContext`);
    }

    const isMutable = typeof params.x === 'number' && typeof params.y === 'number';

    let currentX = typeof params.x === 'number' ? params.x : 0;
    let currentY = typeof params.y === 'number' ? params.y : 0;

    const getX = isMutable ? () => currentX : valueContext.createThunk(params.x);
    const getY = isMutable ? () => currentY : valueContext.createThunk(params.y);

    // Resolve point style
    const pointStyle = resolveFeatureStyle('point', 'point', context, display, []);
    const visible = resolveFeatureVisibility('point', featureTheme, display, []);
    const isFixed = pointStyle.fixed ?? true;
    const isDraggable = !isFixed && isMutable;

    // Draggable points must use literal coords to be JSXGraph "free" points.
    // Function coords create "dependent" points that JSXGraph won't let the user drag.
    const pointResult = elementFactory.point({
      coords: isDraggable ? [currentX, currentY] : [getX, getY],
      name: params.name ?? id,
      style: {
        ...pointStyle,
        visible,
        fixed: isFixed,
      },
    });
    objects.push(pointResult.point);
    if (pointResult.label) {
      objects.push(pointResult.label);
    }

    // When draggable, sync closure state from the point's actual position on drag
    if (isDraggable) {
      const jsxPoint = pointResult.point as unknown as Point;
      jsxPoint.on('drag', () => {
        currentX = jsxPoint.X();
        currentY = jsxPoint.Y();
        context.onParamChange?.(id, { x: currentX, y: currentY });
      });
    }

    const result: CreatedElement = {
      id,
      type: 'point',
      objects,
      getComputedValues: () => ({
        x: getX,
        y: getY,
      }),
      destroy: () => objects.forEach((obj) => context.board.removeObject(obj)),
    };

    if (isMutable) {
      result.getParams = () => ({ x: currentX, y: currentY });
      result.setParams = (newParams: Partial<Record<string, number>>) => {
        if ('x' in newParams && newParams['x'] !== undefined) {
          currentX = newParams['x'];
        }
        if ('y' in newParams && newParams['y'] !== undefined) {
          currentY = newParams['y'];
        }
        if (isDraggable) {
          // Free points must be moved explicitly; board.update() alone won't reposition them
          (pointResult.point as unknown as Point).setPosition(1, [currentX, currentY]);
        }
        context.board.update();
      };
    }

    return result;
  }
}
